class GoogleSheetsService {
  constructor() {
    this.CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    this.SPREADSHEET_ID = process.env.REACT_APP_SPREADSHEET_ID;
    this.RANGE = 'Sheet1!A:Z';
    this.baseUrl = 'https://sheets.googleapis.com/v4/spreadsheets';
    this.driveBaseUrl = 'https://www.googleapis.com/drive/v3';
    this.peopleBaseUrl = 'https://people.googleapis.com/v1';
    this.tokenClient = null;
    this.accessToken = null;
    this.isAuthenticated = false;
    this.authInitialized = false;
    this.SPREADSHEET_ID = null; // Will be set per user
    this.accessToken = null;
    this.refreshToken = null;
    this.expiresAt = null;
    this.userEmail = null;
    
    
    
    // Try to restore token from localStorage
    this.restoreToken();
  }

  async initGoogleAuth() {
    return new Promise((resolve, reject) => {
      // Load Google Identity Services
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
          this.setupAuth();
          resolve();
        };
        
        script.onerror = () => {
          reject(new Error('Failed to load Google Identity Services'));
        };
        
        document.head.appendChild(script);
      } else {
        this.setupAuth();
        resolve();
      }
    });
  }

  setupAuth() {
    console.log('Setting up Google Auth...');
    console.log('Client ID:', this.CLIENT_ID);
    console.log('Google object available:', !!window.google);
    console.log('Google accounts available:', !!(window.google && window.google.accounts));
    
    if (!this.CLIENT_ID || this.CLIENT_ID === 'your_google_oauth2_client_id_here') {
      throw new Error('OAuth2 Client ID not configured. Please check your .env file.');
    }
    
    if (window.google && window.google.accounts) {
      try {
                                  this.tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: this.CLIENT_ID,
            scope: 'https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/user.emails.read https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/directory.readonly',
            callback: (tokenResponse) => {
             console.log('Authentication successful!');
             console.log('Token scopes received:', tokenResponse.scope);
             this.accessToken = tokenResponse.access_token;
             this.isAuthenticated = true;
             // Save token to localStorage
             this.saveToken(tokenResponse);
           },
        });
        console.log('Token client initialized successfully');
      } catch (error) {
        console.error('Error initializing token client:', error);
        throw error;
      }
    } else {
      throw new Error('Google Identity Services not available');
    }
  }

  // Save token to localStorage
  saveToken(tokenResponse) {
    try {
      const tokenData = {
        access_token: tokenResponse.access_token,
        expires_at: Date.now() + (tokenResponse.expires_in * 1000),
        scope: tokenResponse.scope,
        created_at: Date.now(),
        last_refresh: Date.now()
      };
      
      // Save to localStorage
      localStorage.setItem('google_auth_token', JSON.stringify(tokenData));
      
      // Also save to sessionStorage as backup
      sessionStorage.setItem('google_auth_token', JSON.stringify(tokenData));
      
      // Set a flag to indicate we're logged in
      localStorage.setItem('is_logged_in', 'true');
      sessionStorage.setItem('is_logged_in', 'true');
      
      console.log('Token saved to multiple storage locations');
    } catch (error) {
      console.error('Error saving token to storage:', error);
    }
  }

  // Restore token from localStorage
  restoreToken() {
    try {
      // Try localStorage first
      let tokenData = localStorage.getItem('google_auth_token');
      
      // If not in localStorage, try sessionStorage
      if (!tokenData) {
        tokenData = sessionStorage.getItem('google_auth_token');
      }
      
      if (tokenData) {
        const parsed = JSON.parse(tokenData);
        const now = Date.now();
        
        // Check if token is still valid (with 10 minute buffer)
        if (parsed.expires_at > now + (10 * 60 * 1000)) {
          this.accessToken = parsed.access_token;
          this.isAuthenticated = true;
          
          // Update last refresh time
          parsed.last_refresh = now;
          localStorage.setItem('google_auth_token', JSON.stringify(parsed));
          sessionStorage.setItem('google_auth_token', JSON.stringify(parsed));
          
          console.log('Token restored from storage');
          return true;
        } else {
          // Token expired, remove it from all locations
          this.clearToken();
          console.log('Expired token removed from storage');
        }
      }
    } catch (error) {
      console.error('Error restoring token from storage:', error);
      this.clearToken();
    }
    return false;
  }

  // Check if token is valid by making a test API call
  async validateToken() {
    if (!this.accessToken) return false;
    
    try {
      const response = await fetch(
        `${this.baseUrl}/${this.SPREADSHEET_ID}/values/Sheet1!A1`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (response.status === 401) {
        // Token is invalid, clear it
        this.clearToken();
        return false;
      }
      
      return response.ok;
    } catch (error) {
      console.error('Error validating token:', error);
      this.clearToken();
      return false;
    }
  }

  // Clear token from memory and all storage locations
  clearToken() {
    this.accessToken = null;
    this.refreshToken = null;
    this.expiresAt = null;
    this.isAuthenticated = false;
    this.SPREADSHEET_ID = null;
    this.userEmail = null;
    
    // Clear from all storage locations
    localStorage.removeItem('google_auth_token');
    localStorage.removeItem('is_logged_in');
    localStorage.removeItem('user_spreadsheet_id'); // Also clear stored spreadsheet ID
    sessionStorage.removeItem('google_auth_token');
    sessionStorage.removeItem('is_logged_in');
    
    console.log('Token and spreadsheet ID cleared from all storage locations');
  }

  // Properly logout and revoke Google OAuth token
  async logout() {
    try {
      // Revoke the Google OAuth token if we have one
      if (this.accessToken) {
        await fetch(`https://oauth2.googleapis.com/revoke?token=${this.accessToken}`, {
          method: 'POST'
        });
        console.log('Google OAuth token revoked successfully');
      }
    } catch (error) {
      console.log('Error revoking Google OAuth token:', error);
    } finally {
      // Clear all local state
      this.clearToken();
    }
  }

  // Refresh token if needed
  async refreshTokenIfNeeded() {
    try {
      const tokenData = localStorage.getItem('google_auth_token');
      if (tokenData) {
        const parsed = JSON.parse(tokenData);
        const now = Date.now();
        
        // More aggressive refresh: refresh if token expires in less than 30 minutes
        // This gives us a bigger buffer to prevent expiration
        if (parsed.expires_at < now + (30 * 60 * 1000)) {
          console.log('Token expiring soon, refreshing...');
          this.clearToken();
          await this.initializeAuth();
        }
      }
    } catch (error) {
      console.error('Error checking token refresh:', error);
    }
  }

  // Get current authentication status without triggering auth flows
  getAuthenticationStatus() {
    if (this.isAuthenticated && this.accessToken) {
      try {
        const tokenData = localStorage.getItem('google_auth_token');
        if (tokenData) {
          const parsed = JSON.parse(tokenData);
          const now = Date.now();
          const timeLeft = parsed.expires_at - now;
          const minutesLeft = Math.floor(timeLeft / (1000 * 60));
          
          return {
            isAuthenticated: true,
            timeLeft: minutesLeft,
            status: minutesLeft > 0 ? 'active' : 'expired'
          };
        }
      } catch (error) {
        console.error('Error getting auth status:', error);
      }
    }
    
    return {
      isAuthenticated: false,
      timeLeft: 0,
      status: 'not_authenticated'
    };
  }

  // Check if authentication is needed without triggering full auth flow
  isAuthenticationNeeded() {
    try {
      const tokenData = localStorage.getItem('google_auth_token');
      if (!tokenData) return true;
      
      const parsed = JSON.parse(tokenData);
      const now = Date.now();
      
      // Only need auth if token expires in less than 5 minutes
      return parsed.expires_at < now + (5 * 60 * 1000);
    } catch (error) {
      console.error('Error checking if auth is needed:', error);
      return true;
    }
  }

  // Enhanced token refresh with better error handling
  async forceTokenRefresh() {
    try {
      console.log('Force refreshing token...');
      this.clearToken();
      await this.initializeAuth();
      console.log('Token refresh successful');
      return true;
    } catch (error) {
      console.error('Force token refresh failed:', error);
      return false;
    }
  }

  // Check if we can extend the session without user interaction
  async extendSession() {
    try {
      const tokenData = localStorage.getItem('google_auth_token');
      if (!tokenData) return false;
      
      const parsed = JSON.parse(tokenData);
      const now = Date.now();
      
      // If token expires in more than 15 minutes, we can try to extend
      if (parsed.expires_at > now + (15 * 60 * 1000)) {
        console.log('Session can be extended, attempting silent refresh...');
        
        // Try to get a new token silently
        if (this.tokenClient) {
          return new Promise((resolve) => {
            this.tokenClient.requestAccessToken({ prompt: 'none' });
            
            // Wait for callback to set accessToken
            const checkToken = setInterval(() => {
              if (this.accessToken && this.isAuthenticated) {
                clearInterval(checkToken);
                console.log('Session extended successfully');
                resolve(true);
              }
            }, 100);
            
            // Timeout after 5 seconds
            setTimeout(() => {
              clearInterval(checkToken);
              console.log('Silent refresh failed, will need user interaction');
              resolve(false);
            }, 5000);
          });
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error extending session:', error);
      return false;
    }
  }

  // Initialize authentication at app startup
  async initializeAuth() {
    if (this.isAuthenticated) return true;
    
    try {
      // First, check if we have a valid token from localStorage
      if (this.accessToken && this.isAuthenticated) {
        // Validate the existing token
        const isValid = await this.validateToken();
        if (isValid) {
          console.log('Using existing valid token');
          return true;
        }
      }
      
      // First, ensure Google Identity Services is loaded
      if (!this.authInitialized) {
        await this.initGoogleAuth();
        this.authInitialized = true;
      }
      
      // Wait a bit for the script to fully initialize
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check if we have the token client
      if (!this.tokenClient) {
        throw new Error('Google Auth token client not available');
      }
      
      return new Promise((resolve, reject) => {
        // Only prompt for consent if we don't have a valid token
        this.tokenClient.requestAccessToken({ prompt: this.accessToken ? 'none' : 'consent' });
        
        // Wait for callback to set accessToken
        const checkToken = setInterval(() => {
          if (this.accessToken && this.isAuthenticated) {
            clearInterval(checkToken);
            resolve(true);
          }
        }, 100);
        
        // Timeout after 15 seconds
        setTimeout(() => {
          clearInterval(checkToken);
          reject(new Error('Authentication timeout - please check your OAuth2 Client ID'));
        }, 15000);
      });
    } catch (error) {
      console.error('Authentication initialization error:', error);
      throw error;
    }
  }

  async ensureAuthenticated() {
    if (this.isAuthenticated && this.accessToken && Date.now() < this.expiresAt) {
      return true;
    }

    try {
      await this.initializeAuth();
      
      if (this.tokenClient) {
        const response = await this.tokenClient.requestAccessToken();
        
        if (response.accessToken) {
          this.accessToken = response.accessToken;
          this.isAuthenticated = true;
          
          // Save token for future use
          this.saveToken(response);
          
          // Ensure user has a spreadsheet
          await this.ensureUserSpreadsheet();
          
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Authentication error:', error);
      this.isAuthenticated = false;
      return false;
    }
  }

    // Get students who are currently out (no return time)
  async getStudentsOut(className = null) {
    try {
      console.log('getStudentsOut: Starting...');
      console.log('getStudentsOut: SPREADSHEET_ID:', this.SPREADSHEET_ID);
      console.log('getStudentsOut: className filter:', className);
      
      await this.ensureAuthenticated();
      
      if (!this.SPREADSHEET_ID) {
        console.log('getStudentsOut: No spreadsheet ID, returning empty array');
        return [];
      }
      
      const url = `${this.baseUrl}/${this.SPREADSHEET_ID}/values/${this.RANGE}`;
      console.log('getStudentsOut: Fetching from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('getStudentsOut: Response error text:', errorText);
        throw new Error(`Failed to fetch data from Google Sheets: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      const rows = data.values || [];
      console.log('getStudentsOut: Raw rows from sheet:', rows);
      
      if (rows.length === 0) {
        console.log('getStudentsOut: No rows found, returning empty array');
        return [];
      }

      // Get data rows (skip header row)
      const dataRows = rows.slice(1);
      console.log('getStudentsOut: Data rows (excluding header):', dataRows);
      
      // Filter for students with no return time (5th column should be empty or missing)
      let studentsOut = dataRows
        .filter(row => {
          // Check if row has at least 4 columns and no return time in the 5th column (index 4)
          const hasEnoughColumns = row.length >= 4;
          const noReturnTime = !row[4] || row[4].trim() === '';
          const isStudentRow = hasEnoughColumns && noReturnTime;
          
          console.log(`getStudentsOut: Row ${row[0]} - columns: ${row.length}, no return time: ${noReturnTime}, included: ${isStudentRow}`);
          
          return isStudentRow;
        });

      console.log('getStudentsOut: Students with no return time:', studentsOut);

      // If a class is specified, filter by class (6th column, index 5)
      if (className) {
        studentsOut = studentsOut.filter(row => {
          const rowClass = row[5] || '';
          const matchesClass = rowClass === className;
          console.log(`getStudentsOut: Row ${row[0]} - class: "${rowClass}", filter: "${className}", matches: ${matchesClass}`);
          return matchesClass;
        });
        console.log('getStudentsOut: After class filtering:', studentsOut);
      }
      
      const result = studentsOut.map((row, index) => ({
        id: index + 1,
        name: row[0] || '',
        reason: row[1] || '',
        otherReason: row[2] || '',
        exitTime: row[3] || '',
        returnTime: row[4] || '',
        className: row[5] || ''
      }));
      
      console.log('getStudentsOut: Final result:', result);
      return result;
    } catch (error) {
      console.error('Error fetching students out:', error);
      throw error;
    }
  }

  // Get settings from Sheet2 (passcode and course names)
  async getSettings() {
    try {
      await this.ensureAuthenticated();
      
      // Ensure we have a spreadsheet ID
      if (!this.SPREADSHEET_ID) {
        await this.ensureUserSpreadsheet();
      }
      
      const url = `${this.baseUrl}/${this.SPREADSHEET_ID}/values/Sheet2!A:B`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch settings from Sheet2: ${response.status}`);
      }

      const data = await response.json();
      const rows = data.values || [];
      
      if (rows.length === 0) {
        console.warn('No settings found in Sheet2, using defaults');
        return this.getDefaultSettings();
      }

      // Parse the settings from Sheet2
      const settings = {};
      rows.forEach(row => {
        if (row.length >= 2) {
          const key = row[0];
          const value = row[1];
          
          if (key === 'passcode') {
            settings.passcode = value;
          } else if (key === 'courses') {
            settings.courses = value.split(',').map(course => course.trim()).filter(course => course);
          }
        }
      });

      // Ensure we have both required settings
      if (!settings.passcode || !settings.courses || settings.courses.length === 0) {
        console.warn('Missing required settings, using defaults');
        return this.getDefaultSettings();
      }

      return settings;
    } catch (error) {
      console.error('Error fetching settings:', error);
      console.log('Falling back to default settings');
      return this.getDefaultSettings();
    }
  }

  // Get default settings as fallback
  getDefaultSettings() {
    return {
      passcode: '1234',
      courses: ['Math', 'Science']
    };
  }

  // Add a signout record to Sheet1
  async addSignoutRecord(studentName, className, reason) {
    try {
      await this.ensureAuthenticated();
      
      // Ensure we have a spreadsheet ID
      if (!this.SPREADSHEET_ID) {
        await this.ensureUserSpreadsheet();
      }
      
      const now = new Date();
      const dateStr = now.toLocaleDateString();
      const timeStr = now.toLocaleTimeString();
      
      const newRecord = [
        studentName,
        className,
        dateStr,
        timeStr,
        reason,
        'Out' // Status
      ];
      
      const url = `${this.baseUrl}/${this.SPREADSHEET_ID}/values/Sheet1!A:F:append?valueInputOption=RAW`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          values: [newRecord]
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add signout record: ${response.status}`);
      }
      
      console.log('Signout record added successfully');
      return true;
    } catch (error) {
      console.error('Error adding signout record:', error);
      throw error;
    }
  }

  // Update settings in Sheet2
  async updateSettings(newSettings) {
    try {
      await this.ensureAuthenticated();
      
      // Ensure we have a spreadsheet ID
      if (!this.SPREADSHEET_ID) {
        await this.ensureUserSpreadsheet();
      }
      
      // Update passcode
      const passcodeUrl = `${this.baseUrl}/${this.SPREADSHEET_ID}/values/Sheet2!B2?valueInputOption=RAW`;
      await fetch(passcodeUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          values: [[newSettings.passcode]]
        })
      });

      // Update courses
      const coursesUrl = `${this.baseUrl}/${this.SPREADSHEET_ID}/values/Sheet2!B3?valueInputOption=RAW`;
      await fetch(coursesUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          values: [[newSettings.courses.join(', ')]]
        })
      });

      console.log('Settings updated successfully in Sheet2');
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }

  // Get student signout count for a specific class within 28 days
  async getStudentSignoutCount(studentName, className) {
    try {
      await this.ensureAuthenticated();
      
      // Ensure we have a spreadsheet ID
      if (!this.SPREADSHEET_ID) {
        await this.ensureUserSpreadsheet();
      }
      
      const url = `${this.baseUrl}/${this.SPREADSHEET_ID}/values/Sheet1!A:F`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status}`);
      }

      const data = await response.json();
      const rows = data.values || [];
      
      if (rows.length <= 1) return 0; // Only headers or no data
      
      // Skip header row and filter by student name, class, and date/exit time
      const fourWeeksAgo = new Date();
      fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
      
      const targetClassLc = (className || '').toLowerCase().trim();
      const signoutCount = rows.slice(1).filter(row => {
        const rowStudentName = (row[0] || '').trim();
        // Per-row schema detection: prefer new positions, fallback to old
        const rowClassName = ((row[5] || '').trim() || (row[1] || '').trim());
        const dateString = ((row[3] || '').trim() || (row[2] || '').trim());
        if (!rowStudentName || !rowClassName || !dateString) return false;
        if (rowStudentName.toLowerCase() !== studentName.toLowerCase()) return false;
        const rowClassLc = rowClassName.toLowerCase();
        // Class match: allow exact, contains, or reverse-contains to handle variants like "Science - Period 2"
        if (targetClassLc) {
          const classMatches = rowClassLc === targetClassLc ||
                               rowClassLc.includes(targetClassLc) ||
                               targetClassLc.includes(rowClassLc);
          if (!classMatches) return false;
        }
        try {
          let when = new Date(dateString);
          if (isNaN(when.getTime())) {
            // Try interpreting as MM/DD/YYYY or similar by splitting
            const parts = dateString.split(/[T, ]/)[0];
            when = new Date(parts);
          }
          return when >= fourWeeksAgo;
        } catch {
          return false;
        }
      }).length;
      
      return signoutCount;
    } catch (error) {
      console.error('Error getting student signout count:', error);
      return 0;
    }
  }

  // Get class signout statistics for all students within 28 days
  async getClassSignoutStats(className) {
    try {
      await this.ensureAuthenticated();
      
      // Ensure we have a spreadsheet ID
      if (!this.SPREADSHEET_ID) {
        await this.ensureUserSpreadsheet();
      }
      
      const url = `${this.baseUrl}/${this.SPREADSHEET_ID}/values/Sheet1!A:F`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status}`);
      }

      const data = await response.json();
      const rows = data.values || [];
      
      if (rows.length <= 1) return []; // Only headers or no data

      
      // Skip header row and filter by class and date
      const fourWeeksAgo = new Date();
      fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
      
      const studentCounts = {};
      
      const targetClassLc = (className || '').toLowerCase().trim();
      rows.slice(1).forEach(row => {
        const rowStudentName = (row[0] || '').trim();
        // Per-row schema detection: prefer new positions, fallback to old
        const rowClassName = ((row[5] || '').trim() || (row[1] || '').trim());
        const dateString = ((row[3] || '').trim() || (row[2] || '').trim());
        if (!rowStudentName || !rowClassName || !dateString) return;
        const rowClassLc = rowClassName.toLowerCase();
        if (targetClassLc) {
          const classMatches = rowClassLc === targetClassLc ||
                               rowClassLc.includes(targetClassLc) ||
                               targetClassLc.includes(rowClassLc);
          if (!classMatches) return;
        }
        try {
          let when = new Date(dateString);
          if (isNaN(when.getTime())) {
            const parts = dateString.split(/[T, ]/)[0];
            when = new Date(parts);
          }
          if (when >= fourWeeksAgo) {
            studentCounts[rowStudentName] = (studentCounts[rowStudentName] || 0) + 1;
          }
        } catch {}
      });
      
      // Convert to array and sort by count (highest first)
      const stats = Object.entries(studentCounts).map(([name, count]) => ({
        name,
        signoutCount: count
      })).sort((a, b) => b.signoutCount - a.signoutCount);
      
      return stats;
    } catch (error) {
      console.error('Error getting class signout stats:', error);
      return [];
    }
  }

  // Record a new signout
  async recordSignout(student, reason, otherReason, exitTime, className) {
    try {
      await this.ensureAuthenticated();
      
      const values = [
        [
          student.name,
          reason,
          otherReason,
          exitTime,
          '', // Empty return time
          className || '' // Class name
        ]
      ];

      const response = await fetch(
        `${this.baseUrl}/${this.SPREADSHEET_ID}/values/${this.RANGE}:append?valueInputOption=USER_ENTERED`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            values: values
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to record signout');
      }

      return await response.json();
    } catch (error) {
      console.error('Error recording signout:', error);
      throw error;
    }
  }

  // Record a student return
  async recordReturn(student, returnTime) {
    try {
      await this.ensureAuthenticated();
      
      // First, find the row with this student's exit record
      const response = await fetch(
        `${this.baseUrl}/${this.SPREADSHEET_ID}/values/${this.RANGE}`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch data for return update');
      }

      const data = await response.json();
      const rows = data.values || [];
      
      if (rows.length === 0) return;

      // Find the row index for this student's exit record
      let rowIndex = -1;
      for (let i = 1; i < rows.length; i++) {
        if (rows[i][0] === student.name && rows[i][3] === student.exitTime && !rows[i][4]) {
          rowIndex = i;
          break;
        }
      }

      if (rowIndex === -1) {
        throw new Error('Student exit record not found');
      }

      // Update the return time in the specific cell
      const returnTimeRange = `Sheet1!E${rowIndex + 1}`;
      const updateResponse = await fetch(
        `${this.baseUrl}/${this.SPREADSHEET_ID}/values/${returnTimeRange}?valueInputOption=USER_ENTERED`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            values: [[returnTime]]
          })
        }
      );

      if (!updateResponse.ok) {
        throw new Error('Failed to update return time');
      }

      return await updateResponse.json();
    } catch (error) {
      console.error('Error recording return:', error);
      throw error;
    }
  }

  // Search for students using Google Directory (not just personal contacts)
  async searchStudents(query) {
    try {
      console.log('searchStudents: Starting search for:', query);
      console.log('searchStudents: accessToken available:', !!this.accessToken);
      
      await this.ensureAuthenticated();
      console.log('searchStudents: Authentication ensured');
      
      if (!query || query.trim().length < 2) {
        console.log('searchStudents: Query too short, returning empty array');
        return []; // Don't search with very short queries
      }

      // Try to find students in the directory using People API
      console.log('searchStudents: Trying People API directory search...');
      
      // Approach 1: Try Google People API with directory search
      try {
        console.log('searchStudents: Trying People API directory search...');
        
        // Try to search the directory directly
        const directorySearchUrl = `${this.peopleBaseUrl}/people:searchDirectoryPeople?query=${encodeURIComponent(query)}&readMask=names,emailAddresses,photos&sources=DIRECTORY_SOURCE_TYPE_DOMAIN_PROFILE&pageSize=20`;
        console.log('searchStudents: Directory search URL:', directorySearchUrl);
        
        const directoryResponse = await fetch(directorySearchUrl, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          }
        });
        
        console.log('searchStudents: Directory search response status:', directoryResponse.status);
        
        if (directoryResponse.ok) {
          const directoryData = await directoryResponse.json();
          const people = directoryData.people || [];
          console.log('searchStudents: Directory search found people:', people.length);
          
          if (people.length > 0) {
            const results = people.map((person, index) => ({
              id: person.resourceName || index + 1,
              name: person.names?.[0]?.displayName || person.emailAddresses?.[0]?.value?.split('@')[0] || 'Unknown',
              email: person.emailAddresses?.[0]?.value || '',
              photo: person.photos?.[0]?.url || null
            }));
            
            console.log('searchStudents: Directory search results:', results);
            return results;
          }
        } else {
          console.log('searchStudents: Directory search not accessible, trying personal contacts...');
        }
      } catch (directoryError) {
        console.log('searchStudents: Directory search error, trying personal contacts:', directoryError.message);
      }

      // Approach 3: Fallback to personal contacts (what we had before)
      try {
        console.log('searchStudents: Falling back to personal contacts...');
        
        const connectionsUrl = `${this.peopleBaseUrl}/people/me/connections?personFields=names,emailAddresses,photos&pageSize=100`;
        console.log('searchStudents: Personal contacts URL:', connectionsUrl);
        
        const response = await fetch(connectionsUrl, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          }
        });

        console.log('searchStudents: Personal contacts response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          const connections = data.connections || [];
          console.log('searchStudents: Found personal contacts:', connections.length);
          
          // Filter connections by name or email
          const queryLower = query.toLowerCase();
          const filteredConnections = connections.filter(connection => {
            const names = connection.names || [];
            const emails = connection.emailAddresses || [];
            
            const nameMatch = names.some(name => 
              name.displayName && name.displayName.toLowerCase().includes(queryLower)
            );
            
            const emailMatch = emails.some(email => 
              email.value && email.value.toLowerCase().includes(queryLower)
            );
            
            return nameMatch || emailMatch;
          });
          
          console.log('searchStudents: Filtered personal contacts:', filteredConnections.length);
          
          const results = filteredConnections.map((connection, index) => {
            let displayName = 'Unknown';
            if (connection.names && connection.names.length > 0) {
              const primaryName = connection.names.find(name => name.metadata?.primary) || connection.names[0];
              displayName = primaryName.displayName || 
                (primaryName.givenName && primaryName.familyName ? 
                  `${primaryName.givenName} ${primaryName.familyName}` : 'Unknown');
            }
            
            if (displayName === 'Unknown' && connection.emailAddresses && connection.emailAddresses.length > 0) {
              const email = connection.emailAddresses[0].value;
              displayName = email.split('@')[0];
            }
            
            return {
              id: connection.resourceName || index + 1,
              name: displayName,
              email: connection.emailAddresses?.[0]?.value || '',
              photo: connection.photos?.[0]?.url || null
            };
          });
          
          console.log('searchStudents: Personal contacts results:', results);
          return results;
        }
      } catch (contactsError) {
        console.log('searchStudents: Personal contacts error:', contactsError.message);
      }

      // Final fallback to mock data
      console.warn('searchStudents: All directory search approaches failed, using mock data');
      return this.getMockStudents(query);
      
    } catch (error) {
      console.error('searchStudents: Error in directory search:', error);
      return this.getMockStudents(query);
    }
  }

  // Check token scopes and permissions
  checkTokenScopes() {
    try {
      const tokenData = localStorage.getItem('google_auth_token');
      if (tokenData) {
        const parsed = JSON.parse(tokenData);
        console.log('checkTokenScopes: Token scopes received:', parsed.scope);
        
                 const requiredScopes = [
           'https://www.googleapis.com/auth/spreadsheets',
           'https://www.googleapis.com/auth/contacts.readonly',
           'https://www.googleapis.com/auth/user.emails.read',
           'https://www.googleapis.com/auth/directory.readonly'
         ];
        
        const receivedScopes = parsed.scope ? parsed.scope.split(' ') : [];
        console.log('checkTokenScopes: Received scopes:', receivedScopes);
        
        const missingScopes = requiredScopes.filter(scope => !receivedScopes.includes(scope));
        if (missingScopes.length > 0) {
          console.error('checkTokenScopes: ❌ Missing required scopes:', missingScopes);
        } else {
          console.log('checkTokenScopes: ✅ All required scopes present');
        }
        
        return {
          received: receivedScopes,
          missing: missingScopes,
          hasAllRequired: missingScopes.length === 0
        };
      } else {
        console.log('checkTokenScopes: No token found in localStorage');
        return null;
      }
    } catch (error) {
      console.error('checkTokenScopes: Error checking scopes:', error);
      return null;
    }
  }

  // Test Google Directory access (Admin SDK + People API)
  async testDirectoryPermissions() {
    try {
      console.log('testDirectoryPermissions: Testing Google Directory access...');
      console.log('testDirectoryPermissions: Access token available:', !!this.accessToken);
      console.log('testDirectoryPermissions: Token scopes:', this.accessToken ? 'Available' : 'None');
      
      await this.ensureAuthenticated();
      
      // Test People API directory access methods
      const testCases = [
        {
          name: 'People API Directory Search',
          url: `${this.peopleBaseUrl}/people:searchDirectoryPeople?query=test&readMask=names&sources=DIRECTORY_SOURCE_TYPE_DOMAIN_PROFILE&pageSize=1`,
          description: 'Directory search via People API'
        },
        {
          name: 'People API Personal Profile',
          url: `${this.peopleBaseUrl}/people/me?personFields=names,emailAddresses`,
          description: 'Personal profile information'
        },
        {
          name: 'People API Personal Contacts',
          url: `${this.peopleBaseUrl}/people/me/connections?personFields=names,emailAddresses&pageSize=1`,
          description: 'Personal contacts/connections list'
        }
      ];
      
      for (const testCase of testCases) {
        try {
          console.log(`testDirectoryPermissions: Testing ${testCase.name}...`);
          console.log(`testDirectoryPermissions: URL: ${testCase.url}`);
          
          const response = await fetch(testCase.url, {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
              'Content-Type': 'application/json',
            }
          });
          
          console.log(`testDirectoryPermissions: ${testCase.name} - Status: ${response.status}`);
          
          if (response.ok) {
            const data = await response.json();
            console.log(`testDirectoryPermissions: ✅ ${testCase.name} successful!`);
            
                         if (testCase.name.includes('Directory Search')) {
               console.log(`testDirectoryPermissions: Directory people found: ${data.people?.length || 0}`);
               if (data.people && data.people.length > 0) {
                 console.log('testDirectoryPermissions: Sample directory person:', {
                   resourceName: data.people[0].resourceName,
                   name: data.people[0].names?.[0]?.displayName,
                   email: data.people[0].emailAddresses?.[0]?.value
                 });
               }
             } else if (testCase.name.includes('Personal Profile')) {
               console.log(`testDirectoryPermissions: Profile data available`);
             } else if (testCase.name.includes('Personal Contacts')) {
               console.log(`testDirectoryPermissions: Personal contacts found: ${data.connections?.length || 0}`);
             }
            
            console.log(`testDirectoryPermissions: Description: ${testCase.description}`);
            return true;
          } else {
            const errorText = await response.text();
            console.error(`testDirectoryPermissions: ❌ ${testCase.name} failed:`, response.status, errorText);
            
            // Analyze the error
            if (response.status === 403) {
              console.error(`testDirectoryPermissions: ❌ Permission denied for ${testCase.name} - API may not be enabled or scopes insufficient`);
            } else if (response.status === 400) {
              console.error(`testDirectoryPermissions: ❌ Bad Request for ${testCase.name} - API endpoint or parameters may be incorrect`);
            } else if (response.status === 401) {
              console.error(`testDirectoryPermissions: ❌ Unauthorized for ${testCase.name} - Token may be invalid or expired`);
            }
          }
        } catch (testError) {
          console.error(`testDirectoryPermissions: ❌ Error testing ${testCase.name}:`, testError.message);
        }
      }
      
      // If all tests failed, provide comprehensive diagnostics
      console.error('testDirectoryPermissions: ❌ All directory access tests failed');
      console.error('testDirectoryPermissions: This suggests one of these issues:');
      console.error('testDirectoryPermissions: 1. Required APIs are not enabled in Google Cloud Console');
      console.error('testDirectoryPermissions: 2. OAuth2 scopes are insufficient');
      console.error('testDirectoryPermissions: 3. User does not have directory access permissions');
      console.error('testDirectoryPermissions: 4. Organization does not allow directory access');
      
      return false;
    } catch (error) {
      console.error('testDirectoryPermissions: ❌ Error testing permissions:', error);
      return false;
    }
  }

    // Get or create user-specific spreadsheet
  async ensureUserSpreadsheet() {
    try {
      console.log('=== ensureUserSpreadsheet called ===');
      await this.ensureAuthenticated();
      
      // Check if we already have a spreadsheet ID stored
      if (this.SPREADSHEET_ID) {
        console.log('✅ Already have spreadsheet ID:', this.SPREADSHEET_ID);
        return this.SPREADSHEET_ID;
      }
      
      // First, try to find existing spreadsheet for this user
      console.log('Looking for existing spreadsheet...');
      const existingSheet = await this.findUserSpreadsheet();
      
      if (existingSheet) {
        this.SPREADSHEET_ID = existingSheet.id;
        console.log('✅ Found existing spreadsheet:', existingSheet.name, 'ID:', existingSheet.id);
        
        // Store this ID in localStorage to prevent future searches
        localStorage.setItem('user_spreadsheet_id', existingSheet.id);
        
        // Clean up any duplicates that might exist
        await this.cleanupDuplicateSpreadsheets();
        
        return existingSheet.id;
      } else {
        // Before creating, check localStorage for a previously stored ID
        const storedId = localStorage.getItem('user_spreadsheet_id');
        if (storedId) {
          console.log('🔄 Found stored spreadsheet ID in localStorage:', storedId);
          
          // Verify this ID still exists and is accessible
          try {
            const testResponse = await fetch(`${this.baseUrl}/${storedId}/properties`, {
              headers: {
                'Authorization': `Bearer ${this.accessToken}`,
                'Content-Type': 'application/json',
              }
            });
            
            if (testResponse.ok) {
              this.SPREADSHEET_ID = storedId;
              console.log('✅ Verified stored spreadsheet ID is still valid:', storedId);
              
              // Clean up any duplicates that might exist
              await this.cleanupDuplicateSpreadsheets();
              
              return storedId;
            } else if (testResponse.status === 404) {
              console.log('❌ Stored spreadsheet ID not found (404), removing from storage');
              localStorage.removeItem('user_spreadsheet_id');
            } else {
              console.log('❌ Stored spreadsheet ID verification failed with status:', testResponse.status);
              localStorage.removeItem('user_spreadsheet_id');
            }
          } catch (error) {
            // Handle CORS and other network errors gracefully
            if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
              console.log('❌ CORS/Network error verifying stored ID, treating as invalid');
              localStorage.removeItem('user_spreadsheet_id');
            } else {
              console.log('❌ Error verifying stored spreadsheet ID:', error);
              localStorage.removeItem('user_spreadsheet_id');
            }
          }
        }
        
        // Create new spreadsheet for this user
        console.log('❌ No existing spreadsheet found, creating new one...');
        const newSheet = await this.createUserSpreadsheet();
        this.SPREADSHEET_ID = newSheet.id;
        console.log('🆕 Created new spreadsheet:', newSheet.name, 'ID:', newSheet.id);
        
        // Store the new ID
        localStorage.setItem('user_spreadsheet_id', newSheet.id);
        
        return newSheet.id;
      }
    } catch (error) {
      console.error('❌ Error ensuring user spreadsheet:', error);
      throw error;
    }
  }

  // Find existing spreadsheet for current user
  async findUserSpreadsheet() {
    try {
      console.log('Searching for existing user spreadsheet...');
      
      // Method 1: Search for spreadsheets with our naming convention that the user owns
      try {
        const searchQuery = "name='k12core-attendance-sheets-do_not_edit' and trashed=false and 'me' in owners";
        const url = `${this.driveBaseUrl}/files?q=${encodeURIComponent(searchQuery)}&fields=files(id,name,createdTime,owners)&orderBy=createdTime desc`;
        
        console.log('Search URL:', url);
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          const files = data.files || [];
          
          console.log('Found files via search:', files);
          
          // Return the most recently created one if multiple exist
          if (files.length > 0) {
            const mostRecent = files[0]; // Already sorted by createdTime desc
            console.log('✅ Using existing spreadsheet found via search:', mostRecent);
            return mostRecent;
          }
        } else {
          console.log('Search failed with status:', response.status);
        }
      } catch (searchError) {
        console.log('Search method failed, trying alternative approach:', searchError);
      }
      
      // Method 2: Try to list all spreadsheets and filter by name
      try {
        console.log('Trying alternative method: listing all spreadsheets...');
        const listUrl = `${this.driveBaseUrl}/files?q=mimeType='application/vnd.google-apps.spreadsheet' and trashed=false and 'me' in owners&fields=files(id,name,createdTime)&orderBy=createdTime desc&pageSize=100`;
        
        const listResponse = await fetch(listUrl, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          }
        });
        
        if (listResponse.ok) {
          const listData = await listResponse.json();
          const allSpreadsheets = listData.files || [];
          
          console.log('Total spreadsheets found:', allSpreadsheets.length);
          
          // Filter for our naming convention
          const matchingSheets = allSpreadsheets.filter(file => 
            file.name === 'k12core-attendance-sheets-do_not_edit'
          );
          
          console.log('Matching spreadsheets:', matchingSheets);
          
          if (matchingSheets.length > 0) {
            const mostRecent = matchingSheets[0]; // Already sorted by createdTime desc
            console.log('✅ Using existing spreadsheet found via listing:', mostRecent);
            return mostRecent;
          }
        } else {
          console.log('List method failed with status:', listResponse.status);
        }
      } catch (listError) {
        console.log('List method failed:', listError);
      }
      
      console.log('❌ No existing spreadsheet found via any method');
      return null;
    } catch (error) {
      console.error('❌ Error finding user spreadsheet:', error);
      return null;
    }
  }

  // Create new spreadsheet for user
  async createUserSpreadsheet() {
    try {
      // Double-check we don't already have a spreadsheet ID
      if (this.SPREADSHEET_ID) {
        console.log('⚠️ Already have spreadsheet ID, not creating new one:', this.SPREADSHEET_ID);
        return { id: this.SPREADSHEET_ID, name: 'k12core-attendance-sheets-do_not_edit' };
      }
      
      // Check localStorage one more time
      const storedId = localStorage.getItem('user_spreadsheet_id');
      if (storedId) {
        console.log('⚠️ Found stored ID in localStorage, not creating new one:', storedId);
        this.SPREADSHEET_ID = storedId;
        return { id: storedId, name: 'k12core-attendance-sheets-do_not_edit' };
      }
      
      console.log('🆕 Actually creating new spreadsheet...');
      
      // First create the spreadsheet
      const spreadsheetData = {
        properties: {
          title: 'k12core-attendance-sheets-do_not_edit'
        },
        sheets: [
          {
            properties: {
              title: 'Sheet1',
              gridProperties: {
                rowCount: 1000,
                columnCount: 10
              }
            }
          },
          {
            properties: {
              title: 'Sheet2',
              gridProperties: {
                rowCount: 10,
                columnCount: 2
              }
            }
          }
        ]
      };

      const createResponse = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(spreadsheetData)
      });

      if (!createResponse.ok) {
        throw new Error(`Failed to create spreadsheet: ${createResponse.status}`);
      }

      const spreadsheet = await createResponse.json();
      const spreadsheetId = spreadsheet.spreadsheetId;

      // Set up Sheet2 with default values
      await this.initializeSheet2(spreadsheetId);

      // Set up Sheet1 headers
      await this.initializeSheet1(spreadsheetId);

      return { id: spreadsheetId, name: spreadsheet.properties.title };
    } catch (error) {
      console.error('Error creating user spreadsheet:', error);
      throw error;
    }
  }

  // Initialize Sheet2 with default settings
  async initializeSheet2(spreadsheetId) {
    try {
      const defaultValues = [
        ['Key', 'Value'],
        ['passcode', '1234'],
        ['courses', 'Math, Science']
      ];

      const url = `${this.baseUrl}/${spreadsheetId}/values/Sheet2!A1:B3:append?valueInputOption=RAW`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          values: defaultValues
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to initialize Sheet2: ${response.status}`);
      }

      console.log('Sheet2 initialized with default values');
    } catch (error) {
      console.error('Error initializing Sheet2:', error);
      throw error;
    }
  }

  // Initialize Sheet1 with headers
  async initializeSheet1(spreadsheetId) {
    try {
      const headers = [
        ['Student Name', 'Reason', 'Other Reason', 'Exit Time', 'Return Time', 'Class']
      ];

      const url = `${this.baseUrl}/${spreadsheetId}/values/Sheet1!A1:F1:append?valueInputOption=RAW`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          values: headers
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to initialize Sheet1: ${response.status}`);
      }

      console.log('Sheet1 initialized with headers');
    } catch (error) {
      console.error('Error initializing Sheet1:', error);
      throw error;
    }
  }

  // Get current user's email
  async getCurrentUserEmail() {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get user info: ${response.status}`);
      }

      const userInfo = await response.json();
      this.userEmail = userInfo.email;
      return userInfo.email;
    } catch (error) {
      console.error('Error getting user email:', error);
      return null;
    }
  }

  // Fallback mock data for development/testing
  getMockStudents(query) {
    const mockStudents = [
      { id: 1, name: 'John Smith', email: 'john.smith@school.edu' },
      { id: 2, name: 'Jane Doe', email: 'jane.doe@school.edu' },
      { id: 3, name: 'Mike Johnson', email: 'mike.johnson@school.edu' },
      { id: 4, name: 'Sarah Wilson', email: 'sarah.wilson@school.edu' },
      { id: 5, name: 'David Brown', email: 'david.brown@school.edu' },
      { id: 6, name: 'Emily Davis', email: 'emily.davis@school.edu' },
      { id: 7, name: 'Alex Thompson', email: 'alex.thompson@school.edu' },
      { id: 8, name: 'Maria Garcia', email: 'maria.garcia@school.edu' },
    ];

    if (!query) return mockStudents;
    
    return mockStudents.filter(student =>
      student.name.toLowerCase().includes(query.toLowerCase())
    );
  }

  // Clean up duplicate spreadsheets (keep only the most recent one)
  async cleanupDuplicateSpreadsheets() {
    try {
      console.log('🧹 Starting cleanup of duplicate spreadsheets...');
      
      // Find all spreadsheets with our naming convention
      const searchQuery = "name='k12core-attendance-sheets-do_not_edit' and trashed=false and 'me' in owners";
      const url = `${this.driveBaseUrl}/files?q=${encodeURIComponent(searchQuery)}&fields=files(id,name,createdTime)&orderBy=createdTime desc`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        console.log('❌ Cleanup search failed with status:', response.status);
        return;
      }

      const data = await response.json();
      const files = data.files || [];
      
      if (files.length <= 1) {
        console.log('✅ No duplicates found, cleanup not needed');
        return;
      }
      
      console.log(`🔍 Found ${files.length} spreadsheets, keeping the most recent one`);
      
      // Keep the first one (most recent) and delete the rest
      const keepFile = files[0];
      const deleteFiles = files.slice(1);
      
      console.log('✅ Keeping spreadsheet:', keepFile.id, 'created:', keepFile.createdTime);
      
      // Delete the duplicates
      for (const file of deleteFiles) {
        try {
          console.log('🗑️ Deleting duplicate spreadsheet:', file.id, 'created:', file.createdTime);
          
          const deleteResponse = await fetch(`${this.driveBaseUrl}/files/${file.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            }
          });
          
          if (deleteResponse.ok) {
            console.log('✅ Successfully deleted duplicate:', file.id);
          } else {
            console.log('❌ Failed to delete duplicate:', file.id, 'status:', deleteResponse.status);
          }
        } catch (error) {
          console.error('❌ Error deleting duplicate:', file.id, error);
        }
      }
      
      // Set the kept spreadsheet as our current one
      this.SPREADSHEET_ID = keepFile.id;
      localStorage.setItem('user_spreadsheet_id', keepFile.id);
      
      console.log('🎉 Cleanup completed! Kept spreadsheet:', keepFile.id);
      
    } catch (error) {
      console.error('❌ Error during cleanup:', error);
    }
  }

  // Clear any invalid stored spreadsheet IDs
  clearInvalidStoredIds() {
    try {
      const storedId = localStorage.getItem('user_spreadsheet_id');
      if (storedId) {
        console.log('🧹 Clearing potentially invalid stored spreadsheet ID:', storedId);
        localStorage.removeItem('user_spreadsheet_id');
      }
    } catch (error) {
      console.log('Error clearing stored IDs:', error);
    }
  }

  // Force refresh by clearing all stored data and starting fresh
  async forceRefresh() {
    try {
      console.log('🔄 Force refreshing - clearing all stored data...');
      
      // Clear all stored data
      this.clearToken();
      this.clearInvalidStoredIds();
      
      // Reset service state
      this.SPREADSHEET_ID = null;
      this.isAuthenticated = false;
      
      console.log('✅ Force refresh completed, will create new spreadsheet on next login');
      
    } catch (error) {
      console.error('❌ Error during force refresh:', error);
    }
  }

  // Debug method to check spreadsheet structure
  async debugSpreadsheetStructure() {
    try {
      console.log('🔍 Debugging spreadsheet structure...');
      console.log('🔍 SPREADSHEET_ID:', this.SPREADSHEET_ID);
      console.log('🔍 RANGE:', this.RANGE);
      
      if (!this.SPREADSHEET_ID) {
        console.log('🔍 No spreadsheet ID available');
        return;
      }
      
      const url = `${this.baseUrl}/${this.SPREADSHEET_ID}/values/${this.RANGE}`;
      console.log('🔍 Fetching from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        console.error('🔍 Failed to fetch spreadsheet data:', response.status);
        return;
      }
      
      const data = await response.json();
      const rows = data.values || [];
      
      console.log('🔍 Total rows in spreadsheet:', rows.length);
      console.log('🔍 All rows:', rows);
      
      if (rows.length > 0) {
        console.log('🔍 Header row:', rows[0]);
        console.log('🔍 First data row:', rows[1] || 'No data rows');
        console.log('🔍 Column count in header:', rows[0]?.length || 0);
        if (rows[1]) {
          console.log('🔍 Column count in first data row:', rows[1].length);
        }
      }
      
    } catch (error) {
      console.error('🔍 Error debugging spreadsheet structure:', error);
    }
  }

  // Add sample data for testing
  async addSampleData() {
    try {
      console.log('📝 Adding sample data for testing...');
      
      if (!this.SPREADSHEET_ID) {
        console.log('📝 No spreadsheet ID available');
        return;
      }
      
      const sampleData = [
        ['John Smith', 'Math', '2024-01-15', '10:30 AM', '', 'Math'], // Student out (no return time)
        ['Jane Doe', 'Science', '2024-01-15', '11:00 AM', '2:00 PM', 'Science'], // Student returned
        ['Mike Johnson', 'Math', '2024-01-15', '9:45 AM', '', 'Math'], // Student out (no return time)
      ];
      
      const url = `${this.baseUrl}/${this.SPREADSHEET_ID}/values/Sheet1!A2:F4:append?valueInputOption=RAW`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          values: sampleData
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add sample data: ${response.status}`);
      }
      
      console.log('📝 Sample data added successfully');
      
    } catch (error) {
      console.error('📝 Error adding sample data:', error);
    }
  }
}

export { GoogleSheetsService };
